/* ──────────────────────────────────────────────────────────────────────
 * LeadSmsHistoryCard
 *
 * Per-lead SMS audit panel — every send attempt, newest first, with
 * provider status, error reason, AI-assisted flag, and a body preview.
 *
 * Sources from GET /leads/{id}/sms (visibility-gated server-side).
 * Refresh trigger: bump the `reloadKey` prop after any send so the card
 * reflects the row that QuickActionsCard just persisted.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Stack, Typography, Paper, Chip, CircularProgress, Tooltip, IconButton,
} from "@mui/material";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { fetchLeadSmsList, type SmsHistoryRow } from "./leadApi";

const NAVY = "#1a3353";

interface Props {
  leadId: number;
  /** Bump this to force a reload (e.g. immediately after a send). */
  reloadKey?: number;
}

const STATUS_STYLE: Record<SmsHistoryRow["status"], { bg: string; fg: string; label: string; icon: React.ReactNode }> = {
  sent:        { bg: "#dcfce7", fg: "#166534", label: "Sent",        icon: <CheckCircleOutlineIcon sx={{ fontSize: 11 }} /> },
  delivered:   { bg: "#bbf7d0", fg: "#14532d", label: "Delivered",   icon: <CheckCircleOutlineIcon sx={{ fontSize: 11 }} /> },
  queued:      { bg: "#fef3c7", fg: "#92400e", label: "Queued",      icon: <HourglassEmptyIcon sx={{ fontSize: 11 }} /> },
  failed:      { bg: "#fee2e2", fg: "#991b1b", label: "Failed",      icon: <ErrorOutlineIcon sx={{ fontSize: 11 }} /> },
  undelivered: { bg: "#fee2e2", fg: "#991b1b", label: "Undelivered", icon: <ErrorOutlineIcon sx={{ fontSize: 11 }} /> },
  disabled:    { bg: "#e2e8f0", fg: "#475569", label: "Disabled",    icon: <HourglassEmptyIcon sx={{ fontSize: 11 }} /> },
  received:    { bg: "#dbeafe", fg: "#1e40af", label: "Received",    icon: <CheckCircleOutlineIcon sx={{ fontSize: 11 }} /> },
};

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  if (diff < 60_000)       return "just now";
  if (diff < 3_600_000)    return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000)   return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000)  return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function LeadSmsHistoryCard({ leadId, reloadKey = 0 }: Props) {
  const [rows, setRows]       = useState<SmsHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchLeadSmsList(leadId, 25);
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Couldn't load SMS history");
      setRows([]);
    }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, borderRadius: 2, bgcolor: "#fff",
        border: "1px solid #eef0f4",
        boxShadow: "0 1px 2px rgba(13,27,42,0.04), 0 4px 16px rgba(13,27,42,0.04)",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Stack direction="row" alignItems="center" gap={0.8}>
          <SmsOutlinedIcon sx={{ fontSize: 16, color: "#0891b2" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
            SMS History
          </Typography>
          {rows.length > 0 && (
            <Chip
              size="small"
              label={rows.length}
              sx={{ height: 16, fontSize: 9.5, fontWeight: 700, bgcolor: "#ecfeff", color: "#0891b2" }}
            />
          )}
        </Stack>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading} sx={{ p: 0.3 }}>
            {loading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14, color: "#94a3b8" }} />}
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Typography sx={{ fontSize: 11, color: "#dc2626", py: 1 }}>{error}</Typography>
      )}

      {!error && !loading && rows.length === 0 && (
        <Box sx={{
          p: 1.4, borderRadius: 1.5, textAlign: "center",
          bgcolor: "#f8fafc", border: "1px dashed #e2e8f0",
        }}>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
            No SMS yet. Use Quick Actions → Send SMS.
          </Typography>
        </Box>
      )}

      <Stack gap={0.8}>
        {rows.map((sms) => {
          const style = STATUS_STYLE[sms.status] || STATUS_STYLE.queued;
          const isFailed = sms.status === "failed" || sms.status === "undelivered";
          return (
            <Box
              key={sms.id}
              sx={{
                p: 1, borderRadius: 1.5,
                bgcolor: isFailed ? "#fef2f2" : "#fafbfc",
                border: `1px solid ${isFailed ? "#fecaca" : "#eef0f4"}`,
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                <Chip
                  size="small"
                  icon={style.icon as any}
                  label={style.label}
                  sx={{
                    height: 18, fontSize: 9.5, fontWeight: 700,
                    bgcolor: style.bg, color: style.fg,
                    "& .MuiChip-icon": { color: style.fg, ml: 0.5 },
                    "& .MuiChip-label": { px: 0.7 },
                  }}
                />
                {sms.ai_generated && (
                  <Chip
                    size="small"
                    icon={<AutoAwesomeIcon sx={{ fontSize: 10 }} />}
                    label="AI"
                    sx={{
                      height: 18, fontSize: 9, fontWeight: 700,
                      bgcolor: "#f5f3ff", color: "#6d28d9",
                      "& .MuiChip-icon": { color: "#6d28d9", ml: 0.4 },
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{
                  fontSize: 9.5, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.3,
                }}>
                  {relativeTime(sms.created_at)}
                </Typography>
              </Stack>

              <Typography sx={{
                fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                color: "#475569", mb: 0.4, letterSpacing: 0.2,
              }}>
                {sms.phone}
              </Typography>

              <Typography sx={{
                fontSize: 11.5, color: "#0f172a", lineHeight: 1.4,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {sms.message.length > 220 ? `${sms.message.slice(0, 220)}…` : sms.message}
              </Typography>

              {isFailed && sms.error_message && (
                <Typography sx={{
                  fontSize: 10.5, color: "#b91c1c", mt: 0.5, fontStyle: "italic",
                }}>
                  Provider: {sms.error_message}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
